import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import { COMMON_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics/keyMap';
import {
  MOBILE_APP_ANNIE_METRIC_MAP,
  MOBILE_SENSORTOWER_METRIC_MAP,
  MOBILE_OGDB_METRIC_MAP,
} from '@intelligence/v3/_config/metrics';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import {
  SENSOR_TOWER_TAG_KEYS,
  APPMAGIC_TAG_KEYS,
  APPMAGIC_TAG_DEFAULT_VALUES,
} from '@intelligence/components/Filter/FilterBase/_config';
import {
  commonMobileDefaultFilterValue as commonMobileDefaultFilterValueOriginal,
  commonMobileMauReDownDefaultFilterValue as commonMobileMauReDownDefaultFilterValueOriginal,
} from '@intelligence/pages/TopCharts/_config/topchartFilterCfg';
import {
  hasShortLinkRatio,
  topchartMobileActiveruserValidCfg,
  topchartMobileRevenueValidCfg,
  topchartMobileDownloadValidCfg,
  topchartMobileDatabrainValidCfg,
} from '@intelligence/_config/urlPageValidCfg';
import { defaultActiveuserApiParams as defaultActiveuserApiParamsOriginal } from '@intelligence/pages/TopCharts/MobileGames/ActiveUsers/constant';

import type { IMultiDataSourceTopChartsPageConfigMapping } from '../types';

export const MultiDataSourceTopChartsPageType: IRecord<'activeuser' | 'revenue' | 'downloads' | 'databrain'> = {
  ActiveUsers: 'activeuser',
  Revenue: 'revenue',
  Downloads: 'downloads',
  DataBrainCalibration: 'databrain',
};

const DEFAULT_DATA_SOURCES = ['ogdb', 'sensortower'];

const defaultActiveuserApiParams = {
  ...defaultActiveuserApiParamsOriginal,
  genre_source: 'sensortower',
};

const commonMobileDefaultFilterValue = {
  ...commonMobileDefaultFilterValueOriginal,
  [filterKeyMap.GENRETOR]: {
    ...commonMobileDefaultFilterValueOriginal[filterKeyMap.GENRETOR],
    source: 'sensortower',
  },
};

const commonMobileMauReDownDefaultFilterValue = {
  ...commonMobileMauReDownDefaultFilterValueOriginal,
  [filterKeyMap.GENRETOR]: {
    ...commonMobileMauReDownDefaultFilterValueOriginal[filterKeyMap.GENRETOR],
    source: 'sensortower',
  },
};

export const MultiDataSourceTopChartsPageConfigMapping: IMultiDataSourceTopChartsPageConfigMapping = {
  [MultiDataSourceTopChartsPageType.ActiveUsers]: {
    isDataBrain: false,
    hasUrlShortLink: hasShortLinkRatio.topMobileActiveuser,
    pageLogKey: 'topchartsMobileGamesActiveUsers',
    downloadLogKey: 'topchartMobileGameActiveUserDownLoadClick',
    topChartsType: 'activeuser',
    topchartsNameParams: 'active users',
    metrics: [
      MOBILE_APP_ANNIE_METRIC_MAP[COMMON_METRIC_KEY_MAP.AU],
      MOBILE_SENSORTOWER_METRIC_MAP[COMMON_METRIC_KEY_MAP.AU],
      MOBILE_APP_ANNIE_METRIC_MAP[COMMON_METRIC_KEY_MAP.REVENUE],
      MOBILE_SENSORTOWER_METRIC_MAP[COMMON_METRIC_KEY_MAP.REVENUE],
      MOBILE_APP_ANNIE_METRIC_MAP[COMMON_METRIC_KEY_MAP.ARPU],
      MOBILE_SENSORTOWER_METRIC_MAP[COMMON_METRIC_KEY_MAP.ARPU],
      MOBILE_APP_ANNIE_METRIC_MAP[COMMON_METRIC_KEY_MAP.DOWNLOAD],
      MOBILE_SENSORTOWER_METRIC_MAP[COMMON_METRIC_KEY_MAP.DOWNLOAD],
    ],
    metric: COMMON_METRIC_KEY_MAP.AU,
    latestTimeTypeHasDataFilter: hasDataTimerParams.mobileActiveuser.type,
    urlValidConfig: topchartMobileActiveruserValidCfg,
    defaultParams: {
      ...defaultActiveuserApiParams,
      data_sources: ['sensortower'],
      metrics: ['dau', 'revenue', 'download', 'arpu'],
      order_source: 'sensortower',
      order_metric: 'dau',
      b_data_brain: 1,
    },
    filterCfg: {
      ...filterKeyMap.defineFilterGroups(
        [
          {
            key: 'popular',
            titleKey: 'dbi_filter_group_popular',
            filterKeys: [
              filterKeyMap.RANGE_TIME,
              filterKeyMap.REGION,
              filterKeyMap.PLATFORM,
              filterKeyMap.GENRETOR,
              filterKeyMap.HOT_TAGS_SOURCE,
              filterKeyMap.ALL_Games,
              filterKeyMap.DATASOURCE,
              filterKeyMap.ALL_COMPANYS,
              filterKeyMap.RELEASE_TIME,
            ],
          },
          {
            key: 'game_performance',
            titleKey: 'dbi_filter_group_game_performance',
            filterKeys: [
              filterKeyMap.REVENUE_SCOPE,
              filterKeyMap.ACTIVE_USERS_RANGE,
              filterKeyMap.REVENUE_RANGE,
              filterKeyMap.ARPU_RANGE,
              filterKeyMap.DOWNLOADS_RANGE,
              filterKeyMap.REVENUE_GROWTH,
            ],
          },
          {
            key: 'sensor_tower',
            titleKey: 'dbi_filter_group_sensor_tower',
            iconCode: 'sensortower',
            filterKeys: SENSOR_TOWER_TAG_KEYS,
          },
          {
            key: 'app_magic',
            titleKey: 'dbi_filter_group_app_magic',
            iconCode: 'appmagic',
            filterKeys: APPMAGIC_TAG_KEYS,
          },
          {
            key: 'igdb',
            titleKey: 'dbi_filter_group_igdb',
            iconCode: 'igdb',
            filterKeys: [filterKeyMap.GAME_ENGINE],
          },
        ],
        { ungroupedKeys: [filterKeyMap.MY_FILTER] },
      ),
      enableSticky: true,
      companyFilterParamsDataSources: ['sensortower', 'appannie'],
      companyFilterParamsMetric: 'dau',
      companyFilterParamsGranularity: 'daily',
      companyFilterParamsSourceMetric: { sensortower: ['dau'] },
      defaultValue: commonMobileMauReDownDefaultFilterValue,
      specialParams: {
        dataSourceShowDatabrain: false,
        [filterKeyMap.REVENUE_SCOPE]: {
          controlType: 'segment',
        },
      },
    },
    specialLogic: {
      nikkeLink: true,
    },
  },
  [MultiDataSourceTopChartsPageType.Revenue]: {
    isDataBrain: false,
    hasUrlShortLink: hasShortLinkRatio.topMobileRevenue,
    pageLogKey: 'topchartsMobileGamesRevenue',
    downloadLogKey: 'topchartsMobileGamesRevenueDownloadClick',
    topChartsType: 'revenue',
    topchartsNameParams: 'revenue',
    metrics: [
      MOBILE_APP_ANNIE_METRIC_MAP[COMMON_METRIC_KEY_MAP.REVENUE],
      MOBILE_SENSORTOWER_METRIC_MAP[COMMON_METRIC_KEY_MAP.REVENUE],
      MOBILE_APP_ANNIE_METRIC_MAP[COMMON_METRIC_KEY_MAP.ARPU],
      MOBILE_SENSORTOWER_METRIC_MAP[COMMON_METRIC_KEY_MAP.ARPU],
    ],
    metric: COMMON_METRIC_KEY_MAP.REVENUE,
    latestTimeTypeHasDataFilter: hasDataTimerParams.mobileRevenue.type,
    urlValidConfig: topchartMobileRevenueValidCfg,
    defaultParams: {
      ...defaultActiveuserApiParams,
      data_sources: ['sensortower'],
      metrics: ['revenue', 'arpu'],
      order_source: 'sensortower',
      order_metric: 'revenue',
      b_data_brain: 1,
    },
    filterCfg: {
      ...filterKeyMap.defineFilterGroups(
        [
          {
            key: 'popular',
            titleKey: 'dbi_filter_group_popular',
            filterKeys: [
              filterKeyMap.RANGE_TIME,
              filterKeyMap.REGION,
              filterKeyMap.PLATFORM,
              filterKeyMap.GENRETOR,
              filterKeyMap.HOT_TAGS_SOURCE,
              filterKeyMap.ALL_Games,
              filterKeyMap.DATASOURCE,
              filterKeyMap.ALL_COMPANYS,
              filterKeyMap.RELEASE_TIME,
            ],
          },
          {
            key: 'game_performance',
            titleKey: 'dbi_filter_group_game_performance',
            filterKeys: [
              filterKeyMap.REVENUE_SCOPE,
              filterKeyMap.REVENUE_RANGE,
              filterKeyMap.ARPU_RANGE,
              filterKeyMap.REVENUE_GROWTH,
            ],
          },
          {
            key: 'sensor_tower',
            titleKey: 'dbi_filter_group_sensor_tower',
            iconCode: 'sensortower',
            filterKeys: SENSOR_TOWER_TAG_KEYS,
          },
          {
            key: 'app_magic',
            titleKey: 'dbi_filter_group_app_magic',
            iconCode: 'appmagic',
            filterKeys: APPMAGIC_TAG_KEYS,
          },
          {
            key: 'igdb',
            titleKey: 'dbi_filter_group_igdb',
            iconCode: 'igdb',
            filterKeys: [filterKeyMap.GAME_ENGINE],
          },
        ],
        { ungroupedKeys: [filterKeyMap.MY_FILTER] },
      ),
      enableSticky: true,
      companyFilterParamsDataSources: ['sensortower', 'appannie'],
      companyFilterParamsMetric: 'revenue',
      companyFilterParamsGranularity: 'daily',
      companyFilterParamsSourceMetric: { sensortower: ['revenue'] },
      defaultValue: commonMobileMauReDownDefaultFilterValue,
      specialParams: {
        dataSourceShowDatabrain: false,
        [filterKeyMap.REVENUE_SCOPE]: {
          controlType: 'segment',
        },
      },
    },
    specialLogic: {
      nikkeLink: false,
    },
  },
  [MultiDataSourceTopChartsPageType.Downloads]: {
    isDataBrain: false,
    hasUrlShortLink: hasShortLinkRatio.topMobileDownloads,
    pageLogKey: 'topchartsMobileGamesDownloads',
    downloadLogKey: 'topchartsMobileGamesDownloadsDownloadClick',
    topChartsType: 'downloads',
    topchartsNameParams: 'downloads',
    metrics: [
      MOBILE_APP_ANNIE_METRIC_MAP[COMMON_METRIC_KEY_MAP.DOWNLOAD],
      MOBILE_SENSORTOWER_METRIC_MAP[COMMON_METRIC_KEY_MAP.DOWNLOAD],
    ],
    metric: COMMON_METRIC_KEY_MAP.DOWNLOAD,
    latestTimeTypeHasDataFilter: hasDataTimerParams?.mobileDownload?.type,
    urlValidConfig: topchartMobileDownloadValidCfg,
    defaultParams: {
      ...defaultActiveuserApiParams,
      data_sources: ['sensortower'],
      metrics: ['download'],
      order_source: 'sensortower',
      order_metric: 'download',
      b_data_brain: 1,
    },
    filterCfg: {
      ...filterKeyMap.defineFilterGroups(
        [
          {
            key: 'popular',
            titleKey: 'dbi_filter_group_popular',
            filterKeys: [
              filterKeyMap.RANGE_TIME,
              filterKeyMap.REGION,
              filterKeyMap.PLATFORM,
              filterKeyMap.GENRETOR,
              filterKeyMap.HOT_TAGS_SOURCE,
              filterKeyMap.ALL_Games,
              filterKeyMap.DATASOURCE,
              filterKeyMap.ALL_COMPANYS,
              filterKeyMap.RELEASE_TIME,
            ],
          },
          {
            key: 'game_performance',
            titleKey: 'dbi_filter_group_game_performance',
            filterKeys: [filterKeyMap.REVENUE_SCOPE, filterKeyMap.DOWNLOADS_RANGE],
          },
          {
            key: 'sensor_tower',
            titleKey: 'dbi_filter_group_sensor_tower',
            iconCode: 'sensortower',
            filterKeys: SENSOR_TOWER_TAG_KEYS,
          },
          {
            key: 'app_magic',
            titleKey: 'dbi_filter_group_app_magic',
            iconCode: 'appmagic',
            filterKeys: APPMAGIC_TAG_KEYS,
          },
          {
            key: 'igdb',
            titleKey: 'dbi_filter_group_igdb',
            iconCode: 'igdb',
            filterKeys: [filterKeyMap.GAME_ENGINE],
          },
        ],
        { ungroupedKeys: [filterKeyMap.MY_FILTER] },
      ),
      enableSticky: true,
      stickyHeaderTitle: 'dbi_mobile_games',
      companyFilterParamsDataSources: ['sensortower', 'appannie'],
      companyFilterParamsMetric: 'download',
      companyFilterParamsGranularity: 'daily',
      companyFilterParamsSourceMetric: { sensortower: ['download'] },
      defaultValue: commonMobileMauReDownDefaultFilterValue,
      specialParams: {
        dataSourceShowDatabrain: false,
        [filterKeyMap.REVENUE_SCOPE]: {
          controlType: 'segment',
        },
      },
    },
    specialLogic: {
      nikkeLink: false,
    },
  },
  [MultiDataSourceTopChartsPageType.DataBrainCalibration]: {
    isDataBrain: true,
    hasUrlShortLink: hasShortLinkRatio.topMobileDatabrain,
    pageLogKey: 'topchartsMobileGamesDataBrainCalibration',
    downloadLogKey: 'topchartsMobileGamesDataBrainCalibrationDownloadClick',
    topChartsType: 'databrain',
    topchartsNameParams: 'databrain',
    metrics: [
      MOBILE_APP_ANNIE_METRIC_MAP[COMMON_METRIC_KEY_MAP.AU],
      MOBILE_SENSORTOWER_METRIC_MAP[COMMON_METRIC_KEY_MAP.AU],
      MOBILE_OGDB_METRIC_MAP[COMMON_METRIC_KEY_MAP.AU],
      MOBILE_APP_ANNIE_METRIC_MAP[COMMON_METRIC_KEY_MAP.REVENUE],
      MOBILE_SENSORTOWER_METRIC_MAP[COMMON_METRIC_KEY_MAP.REVENUE],
      MOBILE_OGDB_METRIC_MAP[COMMON_METRIC_KEY_MAP.REVENUE],
      MOBILE_APP_ANNIE_METRIC_MAP[COMMON_METRIC_KEY_MAP.DOWNLOAD],
      MOBILE_SENSORTOWER_METRIC_MAP[COMMON_METRIC_KEY_MAP.DOWNLOAD],
      MOBILE_OGDB_METRIC_MAP[COMMON_METRIC_KEY_MAP.DOWNLOAD],
    ],
    metric: COMMON_METRIC_KEY_MAP.AU,
    latestTimeTypeHasDataFilter: hasDataTimerParams.mobileDatabrain.type,
    urlValidConfig: topchartMobileDatabrainValidCfg,
    defaultParams: {
      ...defaultActiveuserApiParams,
      data_sources: DEFAULT_DATA_SOURCES,
      metrics: ['dau', 'revenue', 'download'],
      order_source: 'ogdb',
      order_metric: 'dau',
      b_data_brain: 0,
    },
    filterCfg: {
      ...filterKeyMap.defineFilterGroups(
        [
          {
            key: 'popular',
            titleKey: 'dbi_filter_group_popular',
            filterKeys: [
              filterKeyMap.RANGE_TIME,
              filterKeyMap.REGION,
              filterKeyMap.GENRETOR,
              filterKeyMap.HOT_TAGS_SOURCE,
              filterKeyMap.DATASOURCE,
              filterKeyMap.ALL_COMPANYS,
              filterKeyMap.RELEASE_TIME,
            ],
          },
          {
            key: 'game_performance',
            titleKey: 'dbi_filter_group_game_performance',
            filterKeys: [
              filterKeyMap.REVENUE_SCOPE,
              filterKeyMap.ACTIVE_USERS_RANGE,
              filterKeyMap.REVENUE_RANGE,
              filterKeyMap.DOWNLOADS_RANGE,
            ],
          },
          {
            key: 'sensor_tower',
            titleKey: 'dbi_filter_group_sensor_tower',
            iconCode: 'sensortower',
            filterKeys: SENSOR_TOWER_TAG_KEYS,
          },
          {
            key: 'app_magic',
            titleKey: 'dbi_filter_group_app_magic',
            iconCode: 'appmagic',
            filterKeys: APPMAGIC_TAG_KEYS,
          },
          {
            key: 'igdb',
            titleKey: 'dbi_filter_group_igdb',
            iconCode: 'igdb',
            filterKeys: [filterKeyMap.GAME_ENGINE],
          },
        ],
        { ungroupedKeys: [filterKeyMap.MY_FILTER] },
      ),
      enableSticky: true,
      companyFilterParamsDataSources: ['ogdb', 'sensortower', 'appannie'],
      companyFilterParamsMetric: 'dau',
      companyFilterParamsGranularity: 'monthly',
      companyFilterParamsSourceMetric: { appannie: ['dau'] },
      defaultValue: {
        ...commonMobileDefaultFilterValue,
        ...APPMAGIC_TAG_DEFAULT_VALUES,
        [filterKeyMap.DATASOURCE]: {
          value: DEFAULT_DATA_SOURCES,
        },
      },
      specialParams: {
        dataSourceShowDatabrain: true,
        [filterKeyMap.REVENUE_SCOPE]: {
          controlType: 'segment',
        },
      },
    },
    specialLogic: {
      nikkeLink: false,
    },
  },
};
